import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatSaturday, formatTimeRange } from "../lib/fmt";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft, Plus, Download, MapPin, Clock, Trash2, UserX, RotateCcw,
  Truck, ClipboardList, PackageOpen, UserPlus,
} from "lucide-react";

const ROLE_ICON = {
  delivery_driver: Truck,
  delivery_coordinator: ClipboardList,
  pickup_driver: PackageOpen,
};

function StatusBadge({ status }) {
  const cfg = {
    confirmed: { cls: "bg-brand-green/15 text-brand-green-dark", label: "Confirmed" },
    cancelled: { cls: "bg-slate-200 text-slate-600", label: "Cancelled" },
    no_show:   { cls: "bg-rose-100 text-rose-700", label: "No-show" },
  }[status] || { cls: "bg-slate-100 text-slate-600", label: status };
  return <span data-testid={`status-${status}`} className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

export default function ShiftDetail() {
  const { id } = useParams();
  const { user, checking } = useAuth();
  const navigate = useNavigate();
  const [shift, setShift] = useState(null);
  const [editing, setEditing] = useState(false);
  const [shiftForm, setShiftForm] = useState(null);
  const [addCtx, setAddCtx] = useState(null);   // {role}
  const [addForm, setAddForm] = useState({ name: "", email: "", phone: "" });
  const [addMode, setAddMode] = useState("member"); // "member" | "guest"
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [members, setMembers] = useState([]);
  const [working, setWorking] = useState(false);

  useEffect(() => { if (!checking && !user) navigate("/login"); }, [checking, user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/shifts/${id}`);
      setShift(data);
      setShiftForm({
        start_time: data.start_time, end_time: data.end_time,
        pickup_location: data.pickup_location,
        role_slots: { ...data.role_slots },
      });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { if (user) load(); }, [id, user]);

  // Load members once so the Add-driver dialog can populate its dropdown.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/members").then((r) => setMembers(r.data || [])).catch(() => {});
  }, [user]);

  // Reset the add form each time the dialog opens.
  useEffect(() => {
    if (addCtx) {
      setAddForm({ name: "", email: "", phone: "" });
      setSelectedMemberId("");
      setAddMode("member");
    }
  }, [addCtx]);

  const saveShift = async () => {
    setWorking(true);
    try {
      const body = {
        start_time: shiftForm.start_time,
        end_time: shiftForm.end_time,
        pickup_location: shiftForm.pickup_location,
        role_slots: {
          delivery_driver: Number(shiftForm.role_slots.delivery_driver || 0),
          delivery_coordinator: Number(shiftForm.role_slots.delivery_coordinator || 0),
          pickup_driver: Number(shiftForm.role_slots.pickup_driver || 0),
        },
      };
      await api.put(`/admin/shifts/${id}`, body);
      toast.success("Shift updated.");
      setEditing(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const addDriver = async () => {
    setWorking(true);
    try {
      if (addMode === "member") {
        if (!selectedMemberId) {
          setWorking(false);
          return toast.error("Pick a member from the list.");
        }
        await api.post(`/admin/shifts/${id}/signups`, { member_id: selectedMemberId, role: addCtx.role });
      } else {
        if (!addForm.name || !addForm.email || !addForm.phone) {
          setWorking(false);
          return toast.error("Fill in all fields.");
        }
        await api.post(`/admin/shifts/${id}/signups`, { ...addForm, role: addCtx.role });
      }
      toast.success("Volunteer added.");
      setAddCtx(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const setStatus = async (s, status) => {
    try { await api.patch(`/admin/signups/${s.id}/status`, { status }); load(); toast.success("Status updated."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeSignup = async (s) => {
    if (!window.confirm(`Remove ${s.name} from this shift?`)) return;
    try { await api.delete(`/admin/signups/${s.id}`); load(); toast.success("Volunteer removed."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteShift = async () => {
    if (!window.confirm("Delete this shift and all signups?")) return;
    try { await api.delete(`/admin/shifts/${id}`); toast.success("Shift deleted."); navigate("/admin"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!user) return null;
  if (!shift) return (
    <div className="min-h-screen surface-dashboard">
      <Header variant="admin" />
      <div className="max-w-4xl mx-auto px-4 py-10"><Skeleton className="h-48 rounded-xl" /></div>
    </div>
  );

  const roleEntries = Object.entries(shift.roles || {});

  return (
    <div className="min-h-screen surface-dashboard">
      <Header variant="admin" />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        <Link to="/admin" data-testid="back-to-dashboard-link" className="inline-flex items-center text-sm text-slate-600 hover:text-brand-blue font-medium">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to dashboard
        </Link>

        <div className="card-elevated rounded-xl p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <span className="inline-block text-xs uppercase tracking-[0.22em] font-semibold text-brand-blue mb-1">{shift.route?.name}</span>
              <h1 data-testid="shift-title" className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{formatSaturday(shift.date)}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-600">
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-slate-400" />{formatTimeRange(shift.start_time, shift.end_time)}</span>
                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" />{shift.pickup_location}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <a data-testid="export-shift-button" href={`${API_BASE}/admin/shifts/${shift.id}/export`} className="inline-flex items-center px-3 py-2 rounded-lg border border-slate-300 hover:border-brand-blue hover:text-brand-blue text-sm font-medium">
                <Download className="w-4 h-4 mr-1.5" /> Export CSV
              </a>
              <Button data-testid="edit-shift-button" variant="outline" onClick={() => setEditing(true)} className="border-slate-300 hover:border-brand-blue hover:text-brand-blue">Edit shift</Button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Positions</div>
              <div className="font-heading text-2xl font-bold text-slate-900">{shift.total_slots}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Filled</div>
              <div className="font-heading text-2xl font-bold text-brand-green-dark">{shift.filled}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Open</div>
              <div className={`font-heading text-2xl font-bold ${shift.open === 0 ? "text-slate-400" : "text-amber-600"}`}>{shift.open}</div>
            </div>
          </div>
        </div>

        <section className="space-y-3">
          <h2 className="font-heading text-xl font-semibold text-slate-900">Positions</h2>
          <div className="grid gap-3">
            {roleEntries.map(([roleKey, roleData]) => {
              const Icon = ROLE_ICON[roleKey];
              return (
                <div key={roleKey} data-testid={`role-section-${roleKey}`} className="card-elevated rounded-xl p-5">
                  <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${roleData.open === 0 ? "bg-brand-green/15 text-brand-green-dark" : "bg-brand-blue/10 text-brand-blue"}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900">{roleData.label}</h3>
                        <div className="text-xs text-slate-500">{roleData.filled} of {roleData.slots} filled</div>
                      </div>
                    </div>
                    <Button
                      data-testid={`role-add-${roleKey}`}
                      size="sm"
                      disabled={roleData.open === 0}
                      onClick={() => { setAddCtx({ role: roleKey, label: roleData.label }); setAddForm({ name: "", email: "", phone: "" }); }}
                      className="bg-brand-blue hover:bg-brand-blue-dark disabled:opacity-40"
                    >
                      <UserPlus className="w-4 h-4 mr-1.5" /> Add volunteer
                    </Button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {(roleData.signups || []).length === 0 && (
                      <div className="text-xs text-slate-400 italic py-2">No volunteer yet — slot is open.</div>
                    )}
                    {(roleData.signups || []).map((s) => (
                      <div key={s.id} data-testid={`role-roster-${s.id}`} className="flex flex-wrap items-center gap-3 py-3">
                        <div className="flex-1 min-w-[180px]">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{s.name}</span>
                            <StatusBadge status={s.status} />
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{s.email} · {s.phone}</div>
                        </div>
                        <div className="flex gap-2 ml-auto">
                          {s.status !== "confirmed" && (
                            <Button size="sm" variant="ghost" data-testid={`restore-${s.id}`} onClick={() => setStatus(s, "confirmed")} className="text-slate-600 hover:text-brand-blue">
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Restore
                            </Button>
                          )}
                          {s.status !== "no_show" && (
                            <Button size="sm" variant="ghost" data-testid={`noshow-${s.id}`} onClick={() => setStatus(s, "no_show")} className="text-slate-600 hover:text-rose-600">
                              <UserX className="w-3.5 h-3.5 mr-1" /> No-show
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" data-testid={`remove-${s.id}`} onClick={() => removeSignup(s)} className="text-slate-500 hover:text-rose-600">
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <div>
          <Button data-testid="delete-shift-button" variant="ghost" onClick={deleteShift} className="text-slate-500 hover:text-rose-600">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete this shift
          </Button>
        </div>
      </main>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent data-testid="shift-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit shift</DialogTitle></DialogHeader>
          {shiftForm && (
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-semibold">Pickup location</Label>
                <Input data-testid="shift-pickup-input" value={shiftForm.pickup_location} onChange={(e) => setShiftForm({ ...shiftForm, pickup_location: e.target.value })} className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-semibold">Start</Label>
                  <Input type="time" data-testid="shift-start-input" value={shiftForm.start_time} onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">End</Label>
                  <Input type="time" data-testid="shift-end-input" value={shiftForm.end_time} onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50/50">
                <Label className="text-sm font-semibold mb-2 block">Positions for this Saturday</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: "delivery_driver", label: "Delivery Driver" },
                    { key: "delivery_coordinator", label: "Coordinator" },
                    { key: "pickup_driver", label: "Pick Up Driver" },
                  ].map((r) => (
                    <div key={r.key}>
                      <div className="text-[11px] font-semibold text-slate-600 mb-1">{r.label}</div>
                      <Input
                        type="number"
                        min={0}
                        max={10}
                        data-testid={`shift-slots-${r.key}`}
                        value={shiftForm.role_slots[r.key] ?? 0}
                        onChange={(e) => setShiftForm({
                          ...shiftForm,
                          role_slots: { ...shiftForm.role_slots, [r.key]: e.target.value },
                        })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            <Button data-testid="save-shift-button" disabled={working} onClick={saveShift} className="bg-brand-blue hover:bg-brand-blue-dark">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!addCtx} onOpenChange={(o) => !o && setAddCtx(null)}>
        <DialogContent data-testid="add-driver-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add a {addCtx?.label}</DialogTitle>
          </DialogHeader>

          <div className="flex gap-2 rounded-lg bg-slate-100 p-1">
            <button
              type="button"
              data-testid="add-mode-member"
              onClick={() => setAddMode("member")}
              className={`flex-1 min-h-[36px] rounded-md text-sm font-semibold transition-colors ${addMode === "member" ? "bg-white text-brand-blue shadow-sm" : "text-slate-600 hover:text-slate-800"}`}
            >
              Pick from members
            </button>
            <button
              type="button"
              data-testid="add-mode-guest"
              onClick={() => setAddMode("guest")}
              className={`flex-1 min-h-[36px] rounded-md text-sm font-semibold transition-colors ${addMode === "guest" ? "bg-white text-brand-blue shadow-sm" : "text-slate-600 hover:text-slate-800"}`}
            >
              Guest volunteer
            </button>
          </div>

          {addMode === "member" ? (
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-sm font-semibold">Member</Label>
                <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
                  <SelectTrigger data-testid="add-member-select" className="mt-1.5">
                    <SelectValue placeholder={`Choose from ${members.length} member${members.length === 1 ? "" : "s"}…`} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id} data-testid={`add-member-option-${m.id}`}>
                        {m.name} · {m.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Contact info comes from their member profile.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-sm font-semibold">Name</Label>
                <Input data-testid="add-driver-name" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Email</Label>
                <Input type="email" data-testid="add-driver-email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Phone</Label>
                <Input type="tel" data-testid="add-driver-phone" value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} className="mt-1.5" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddCtx(null)}>Cancel</Button>
            <Button data-testid="save-add-driver-button" disabled={working} onClick={addDriver} className="bg-brand-blue hover:bg-brand-blue-dark">Add volunteer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
